import React, { useEffect, useState } from 'react'
import { SubHeader } from '../../../components/SubHeader'
import { useLocation, useParams } from 'react-router-dom'
import { Button } from '../../../components/Button'
import api from '../../../services/api'
import { useTranslation } from 'react-i18next'
import { addBreadcrumb } from '../../../redux/reducers/breadcrumb/actions'
import { useAppDispatch } from '../../../redux/hooks'
import { Stack } from '@mui/material'
import { Helmet } from 'react-helmet-async'
import { AthleteTrainingDataList } from './components/AthleteTrainingDataList'
import { InputSearch } from '../../../components/InputSearch'
import { AthleteTrainingForm } from './components/AthleteTrainingForm'
import { TreatEmpty } from 'components/TreatEmpty'
import { LoadingPage } from 'components/LoadingPage'
import { showMessage } from 'redux/reducers/snackbar/snackbar.actions'
import { CopyTrainingToModal } from '../CopyTrainingToModal'
import { Add } from '@mui/icons-material'
import { AthleteTraining } from 'models/interfaces/athlete-training.interface'
import { TreatEmptySuggestion } from 'components/TreatEmptySuggestion'

export const AthleteTrainingList = () => {
  const dispatch = useAppDispatch()
  const { professionalAthleteId, athleteRoutineId } = useParams()
  const { t } = useTranslation()
  const location = useLocation()
  const [data, setData] = useState<AthleteTraining[]>([])
  const [dataSearch, setDataSearch] = useState<AthleteTraining[]>([])
  const [modalVisible, setModalVisible] = React.useState(false)
  const [training, setTraining] = useState<any>()
  const [loading, setLoading] = useState<boolean>(false)
  const [athleteRoutine, setAthleteRoutine] = useState<[]>()
  const [modalCopyTrainingVisible, setModalCopyTrainingVisible] = useState(false)
  const [athleteTraining, setAthleteTraining] = useState<any>()
  const [showInstruction, setShowInstruction] = useState<boolean>(false)

  useEffect(() => {
    const body = document.querySelector('body')
    if (body) {
      body.style.overflow = modalVisible ? 'hidden' : 'auto'
    }
    dataFetch().catch(console.error)
  }, [modalVisible])

  const dataFetch = async () => {
    dispatch(addBreadcrumb({ label: location.state?.name, path: location.pathname }))
    try {
      setLoading(true)
      const { data } = await api.get(`professional-athlete-routines/${athleteRoutineId}/professional-athlete-trainings`)
      if (data) {
        setData(data)
        setDataSearch(data)
        setShowInstruction(data.length > 0 ? false : true)
        if (location.state) {
          setAthleteRoutine(location.state)
        } else {
          setAthleteRoutine(data[0].professionalAthleteRoutine)
        }
      }
    } catch {
      dispatch(showMessage({ message: `${t('general.error')}`, type: 'error' }))
    } finally {
      setLoading(false)
    }
  }

  const handleOpenModal = () => {
    setModalVisible(!modalVisible)
    setTraining(null)
  }

  const handleSearchField = (searchReturn: any) => {
    setDataSearch(searchReturn)
  }

  const handleEdit = (training: any) => {
    setTraining(training)
    setModalVisible(true)
  }

  const handleCheckEmpyt = (athleteTrainingList: AthleteTraining[]) => {
    if (athleteTrainingList.length === 0) {
      setDataSearch([])
      setShowInstruction(true)
    }
  }

  const handleTrainingCopy = (athleteTraining: any) => {
    setAthleteTraining(athleteTraining)
    setModalCopyTrainingVisible(true)
  }

  return (
    <>
      <Helmet>
        <title> Training | DWF </title>
      </Helmet>
      <SubHeader
        goBack={false}
        onPress={handleOpenModal}
        path={location.pathname}
        titleButton={t<string>('training.newTraining')}
      />
      <CopyTrainingToModal
        professionalAthleteId={professionalAthleteId}
        isVisible={modalCopyTrainingVisible}
        athleteTraining={athleteTraining}
        onClose={() => setModalCopyTrainingVisible(false)}
      />
      <AthleteTrainingForm
        isVisible={modalVisible}
        training={training}
        athleteRoutine={athleteRoutine}
        onSubmitted={async () => {
          setModalVisible(false)
          await dataFetch()
        }}
        onClose={() => setModalVisible(false)}
        loading={loading}/>
      <>
        <Stack
          mt={{ xs: 1, sm: 3, md: 3 }}
          mb={{ xs: 2, sm: 0, md: 0 }}
          spacing={1}
          justifyContent="space-between"
          direction={{ xs: 'column', sm: 'row' }}
        >
          <Stack flex={1}>
            <InputSearch
              title={`${t('search.titleTraining')}`}
              data={data}
              params={['labels', 'name', 'note']}
              action={handleSearchField}/>
          </Stack>
          <Stack
            direction={{ xs: 'column', sm: 'row', md: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center', md: 'center' }}
            spacing={2}
          >
            <Button
              title={`${t('training.newTraining')}`}
              startIcon={<Add />}
              onClick={handleOpenModal}
            />
          </Stack>
        </Stack>
        { loading
          ? <LoadingPage />
          : <>
            { dataSearch.length === 0 && data.length > 0
              ? <TreatEmpty subTitleMessage={t('general.searchEmpty')} />
              : data.length === 0
                ? <TreatEmptySuggestion
                  textSuggestion={[
                    t('training.title.suggestion_1', { newTraining: t('training.newTraining') }),
                    t('training.title.suggestion_2')
                  ]}
                  showInstruction={showInstruction}
                />
                : <AthleteTrainingDataList
                  items={dataSearch}
                  onEdit={handleEdit}
                  onAthleteTrainingList={handleCheckEmpyt}
                  onHandleTrainingCopy={handleTrainingCopy}
                />
            }
          </>
        }
      </>
    </>
  )
}
